import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import Swal from "sweetalert2";
import {
  BsBarChartLine,
  BsFillPlusCircleFill,
  BsBoxArrowRight,
  BsActivity,
  BsChevronRight,
  BsLock,
} from "react-icons/bs";
import { AiOutlineDashboard } from "react-icons/ai";
import { FaHome, FaUserCircle } from "react-icons/fa";
import { ImAidKit } from "react-icons/im";
import { HiOutlineShieldCheck } from "react-icons/hi";
import Cookies from "js-cookie";
import {
  getCurrentTheme,
  getRandomMotivationalMessage,
  getCampaignText,
} from "../../config/themeConfig";

const menuOptions = [
  {
    path: "/Sad",
    label: "Painel Inicial",
    icon: <AiOutlineDashboard size={20} />,
    roles: ["Administrador", "Atendimento", "PROFISSIONAL", "SUPERVISAO"],
    description: "Visão geral do sistema",
  },
  {
    path: "/Sad_Formulario",
    label: "Pacientes SAD",
    icon: <BsFillPlusCircleFill size={20} />,
    roles: ["Administrador", "Atendimento", "PROFISSIONAL", "SUPERVISAO"],
    description: "Gestão de pacientes",
  },
  {
    path: "/Instrumento_AvalNive_SAD",
    label: "Avaliação SAD",
    icon: <ImAidKit size={20} />,
    roles: ["Administrador", "Atendimento", "PROFISSIONAL", "SUPERVISAO"],
    description: "Avaliações do SAD",
  },

  {
    path: "/Record_Aval",
    label: "Avaliação de Feridas",
    icon: <BsActivity size={20} />,
    roles: ["Administrador", "Atendimento", "161", "PROFISSIONAL", "SUPERVISAO"],
    description: "Registro de feridas",
  },
  {
    path: "",
    label: "Recuperar SAD - Extra",
    icon: <BsBarChartLine size={20} />,
    roles: [],
    description: "Ferramentas extras",
  },
  {
    path: "",
    label: "Home Care",
    icon: <FaHome size={20} />,
    roles: [],
    description: "Atendimento domiciliar",
  },
];

const AnimatedMenu = () => {
  const navigate = useNavigate();
  const userIdEspecialidade = Cookies.get("IdEspecialidade") || "";
  const UserRole = Cookies.get("UserRole") || "";
  const username = Cookies.get("Username") || "";
  const [hoveredCard, setHoveredCard] = useState<number | null>(null);

  const currentTheme = getCurrentTheme();
  const campaignText = getCampaignText(currentTheme);
  const motivationalMessage = getRandomMotivationalMessage(currentTheme);

 const handleLogout = () => {
    const cookiesToRemove = [
        "Frontend",
        "UserRole", 
        "IdEspecialidade", 
        "Username",
        "atestado",
        "cdConselho",
        "dsEspecialidade",
        "emailProfissional",
        "estaExcluído",  // Note o acento
        "guiaSadt",
        "idEspecialidade", // estava IdEspecialidade em maiúscula
        "idProfissional",
        "laudo",
        "loginProfissional",
        "loginTime",
        "nmProfissional",
        "receituarioComum",  // tinha erro de digitação na sua lista
        "receituarioEspecial", // tinha erro de digitação
        "sadAccess",
        "UserId",
        "Username",  // repetido
        "UserRole"   // repetido
    ];

    // Remover duplicados
    const uniqueCookies = [...new Set(cookiesToRemove)];

    uniqueCookies.forEach((item) => {
        // Tentar remover com path raiz
        Cookies.remove(item, { path: "/" });
        // Tentar remover sem path (para cookies setados sem path)
        Cookies.remove(item);
        // Se estiver em subdomínio, pode precisar do domínio
        Cookies.remove(item, { path: "/", domain: window.location.hostname });
    });

    // DEBUG: Verificar o que ainda está nos cookies
    console.log("Cookies após remoção:", document.cookie);

    Swal.fire({
        title: "Confirmado!",
        text: "Você foi desconectado com sucesso!",
        icon: "success",
        showConfirmButton: false,
        timer: 1700,
        timerProgressBar: true,
        willClose: () => navigate("/"),
    });
};
  const availableModules = menuOptions.filter(
    (opt) =>
      opt.roles.includes(userIdEspecialidade) || opt.roles.includes(UserRole)
  ).length;

  const totalModules = menuOptions.length;
  const accessPercentage = Math.round((availableModules / totalModules) * 100);

  // Helper function para texto do tema
  const getThemeText = () => {
    switch (currentTheme.theme) {
      case "yellow":
        return "text-yellow-600";
      case "pink":
        return "text-pink-600";
      case "blue":
        return "text-blue-600";
      case "red":
        return "text-red-600";
      default:
        return "text-blue-600";
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
      {/* Header Superior */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            {/* Logo */}
            <div className="flex items-center">
              <div className="bg-gray-600 p-2 rounded-lg">
                <img
                  src="https://i0.wp.com/afrafepsaude.com.br/wp-content/uploads/2021/06/logo.png?w=692&ssl=1"
                  alt="Logo Afrafepsaúde"
                  className="h-8 w-auto"
                />
              </div>
              <div className="ml-4">
                <h1 className="text-lg font-semibold text-gray-900">SAD </h1>
              </div>
            </div>

            {/* User Info */}
            <div className="flex items-center space-x-4">
              <div className="flex items-center space-x-3">
                <div className="text-right">
                  <p className="text-sm font-medium text-gray-900">
                    {username}
                  </p>
                  <p className="text-xs text-gray-500 capitalize">
                    {UserRole.toLowerCase()}
                  </p>
                </div>
                <div className="relative">
                  <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                    <FaUserCircle className="w-6 h-6 text-blue-600" />
                  </div>
                  <div className="absolute bottom-0 right-0 w-3 h-3 bg-green-500 rounded-full border-2 border-white"></div>
                </div>
              </div>
              <button
                onClick={handleLogout}
                className="flex items-center space-x-2 px-4 py-2 text-sm text-red-600 hover:text-red-700 hover:bg-red-50 rounded-lg transition-colors"
              >
                <BsBoxArrowRight className="w-4 h-4" />
                <span>Sair</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Conteúdo Principal */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Banner da Campanha */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className={`mb-8 rounded-xl bg-gradient-to-r ${currentTheme.gradients.brand} p-6 shadow-lg`}
        >
          <div className="flex items-center justify-between">
            <div>
              <div className="flex items-center space-x-3 mb-2">
                <div className="text-2xl">{currentTheme.decorations.icon}</div>
                <h2 className="text-2xl font-bold text-white">
                  {campaignText.title}
                </h2>
                <div className="text-2xl">{currentTheme.decorations.emoji}</div>
              </div>
              <p className="text-white/90 text-lg">{campaignText.subtitle}</p>
            </div>
            <div className="hidden md:block">
              <div className="bg-white/20 backdrop-blur-sm px-4 py-2 rounded-lg">
                <p className="text-white text-sm font-medium">
                  {motivationalMessage}
                </p>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Módulos Disponíveis */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h2 className="text-2xl font-bold text-gray-900">
                Módulos Disponíveis
              </h2>
              <p className="text-gray-900 mt-1">
                {availableModules} de {totalModules} módulos acessíveis
              </p>
            </div>
            <div className="flex items-center space-x-4">
              <div className="flex items-center space-x-2">
                <HiOutlineShieldCheck className="w-5 h-5 text-blue-600" />
                <span className="text-sm font-medium text-gray-700">
                  Acesso Seguro
                </span>
              </div>
              {/* Barra de Progresso */}
              <div className="w-48">
                <div className="flex justify-between text-sm text-gray-900 mb-1">
                  <span>Nível de acesso</span>
                  <span>{accessPercentage}%</span>
                </div>
                <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                  <div
                    className={`h-full ${currentTheme.colors.primary} rounded-full`}
                    style={{ width: `${accessPercentage}%` }}
                  ></div>
                </div>
              </div>
            </div>
          </div>

          {/* Grid de Módulos */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {menuOptions.map((option, index) => {
              const hasAccess =
                option.roles.includes(userIdEspecialidade) ||
                option.roles.includes(UserRole);

              return (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                  onMouseEnter={() => setHoveredCard(index)}
                  onMouseLeave={() => setHoveredCard(null)}
                >
                  {hasAccess ? (
                    <Link to={option.path} className="block">
                      <div
                        className={`h-full bg-white rounded-xl border border-gray-200 p-5 hover:shadow-lg transition-all duration-300 ${
                          hoveredCard === index
                            ? "border-blue-500 shadow-md"
                            : ""
                        }`}
                      >
                        <div className="flex items-start justify-between">
                          <div
                            className={`p-3 rounded-lg ${currentTheme.colors.primary} bg-opacity-10`}
                          >
                            <div className={getThemeText()}>{option.icon}</div>
                          </div>
                          <BsChevronRight
                            className={`w-5 h-5 text-gray-600 transition-transform ${
                              hoveredCard === index
                                ? "translate-x-1 text-blue-500"
                                : ""
                            }`}
                          />
                        </div>
                        <h3 className="font-semibold text-gray-900 mt-4 mb-2">
                          {option.label}
                        </h3>
                        <p className="text-sm textgray-700">
                          {option.description}
                        </p>
                        <div className="mt-4 pt-4 border-t border-gray-100">
                          <span className="inline-flex items-center text-sm text-blue-600 font-medium">
                            Acessar módulo
                          </span>
                        </div>
                      </div>
                    </Link>
                  ) : (
                    // Layout para módulos restritos - REVISADO
                    <div className="h-full bg-gradient-to-br from-gray-50 to-gray-100 rounded-xl border border-gray-300 p-5 relative">
                      {/* Overlay de bloqueio */}
                      <div className="absolute top-4 right-4">
                        <div className="bg-gray-200 p-2 rounded-full">
                          <BsLock className="w-4 h-4 text-gray-500" />
                        </div>
                      </div>

                      {/* Conteúdo principal */}
                      <div className="opacity-100">
                        <div className="p-3 rounded-lg bg-gray-200 mb-4">
                          <div className="text-gray-500">{option.icon}</div>
                        </div>
                        <h3 className="font-semibold text-gray-700 mt-4 mb-2">
                          {option.label}
                        </h3>
                        <p className="text-sm textgray-700 mb-4">
                          {option.description}
                        </p>
                      </div>

                      {/* Mensagem de acesso restrito */}
                      <div className="mt-4 pt-4 border-t border-gray-200">
                        <div className="flex items-center space-x-2">
                          <BsLock className="w-4 h-4 text-gray-500" />
                          <span className="text-sm font-medium text-gray-500">
                            Acesso restrito
                          </span>
                        </div>
                        <p className="text-xs text-gray-800 mt-1">
                          Permissão necessária: {UserRole}
                        </p>
                      </div>

                      {/* Efeito visual de bloqueio */}
                      <div className="absolute inset-0 pointer-events-none">
                        <div className="absolute inset-0 bg-gradient-to-t from-white/50 to-transparent opacity-60 rounded-xl"></div>
                        <div className="absolute inset-0 border-2 border-dashed border-gray-300/50 rounded-xl m-1"></div>
                      </div>
                    </div>
                  )}
                </motion.div>
              );
            })}
          </div>
        </div>

        {/* Status e Informações */}
        <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {/* Status de Acesso */}
            <div>
              <h3 className="font-semibold text-gray-900 mb-4">
                Status de Acesso
              </h3>
              <div className="space-y-3">
                <div>
                  <div className="flex justify-between text-sm text-gray-900 mb-1">
                    <span>Módulos disponíveis</span>
                    <span className="font-medium">
                      {availableModules}/{totalModules}
                    </span>
                  </div>
                  <div className="h-2 bg-gray-200 rounded-full">
                    <div
                      className={`h-full ${currentTheme.colors.primary} rounded-full`}
                      style={{ width: `${accessPercentage}%` }}
                    ></div>
                  </div>
                </div>
                <div className="flex items-center space-x-2">
                  <div
                    className={`w-3 h-3 rounded-full ${currentTheme.colors.primary}`}
                  ></div>
                  <span className="text-sm text-gray-900">
                    Perfil: <span className="font-medium">{UserRole}</span>
                  </span>
                </div>
              </div>
            </div>

            {/* Campanha Atual */}
            <div>
              <div className="flex items-start space-x-3">
                <div
                  className={`p-2 rounded-lg ${currentTheme.colors.primary} bg-opacity-10`}
                >
                  <div className={getThemeText()}>
                    {currentTheme.decorations.icon}
                  </div>
                </div>
                <div>
                  <p className="font-medium text-gray-900">
                    {campaignText.title}
                  </p>
                  <p className="text-sm textgray-700 mt-1">
                    {currentTheme.messages.subtitle}
                  </p>
                </div>
              </div>
            </div>

            {/* Sistema */}
            <div>
              <h3 className="font-semibold text-gray-900 mb-4">Sistema</h3>
              <div className="space-y-2">
                <div className="flex items-center justify-between text-sm">
                  <span className="textgray-700">Versão</span>
                  <span className="font-medium">2.0.0</span>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="textgray-700">Status</span>
                  <span className="flex items-center">
                    <span className="w-2 h-2 bg-green-500 rounded-full mr-2 animate-pulse"></span>
                    <span className="font-medium text-green-600">Online</span>
                  </span>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="textgray-700">Sessão</span>
                  <span className="font-medium">Ativa</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className="border-t bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex flex-col md:flex-row justify-between items-center">
            <div className="mb-4 md:mb-0">
              <p className="text-sm textgray-700">
                © {new Date().getFullYear()} Afrafep Saúde - Sistema de
                Atendimento Domiciliar
              </p>
            </div>
            <div className="flex items-center space-x-4">
              <div className="flex items-center space-x-2">
                <div
                  className={`w-2 h-2 rounded-full ${currentTheme.colors.primary}`}
                ></div>
                <span className="text-sm textgray-700">
                  {campaignText.title}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AnimatedMenu;
